import React, { useRef, useState, useMemo } from 'react'
import {
  ButtonBase,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogContent,
  Grid,
  DialogActions
} from '@material-ui/core'
import { Paperclip, Trash2 } from 'lucide-react'
import { getBackendURL } from '../../services/config'
import { useStyles } from './style'
import { ButtonSecondary } from '../ButtonSecondary'
import AudioPlayer from '../AudioPlayer'

const DEFAULT_IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'gif', 'png', 'bmp']
const DEFAULT_AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg']

export const AttachmentInput = ({
  mediaPath,
  mediaName,
  label,
  displayName = 'item',
  size = 'medium',
  type = 'all',
  allowedImageExtensions,
  allowedAudioExtensions,
  showMediaName = true,
  helperText,
  onFileSelect,
  onFileRemove
}) => {
  const classes = useStyles({ size })
  const fileInputRef = useRef(null)

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const imageExtensions = allowedImageExtensions || DEFAULT_IMAGE_EXTENSIONS
  const audioExtensions = allowedAudioExtensions || DEFAULT_AUDIO_EXTENSIONS

  const getMediaUrl = path => {
    if (!path) return null
    if (path.startsWith('http') || path.startsWith('blob:')) {
      return path
    }
    return `${getBackendURL()}/public/${path}`
  }

  const derivedMediaName = useMemo(() => {
    if (mediaName) return mediaName
    if (mediaPath) return mediaPath.split('/').pop().split('?')[0]
    return ''
  }, [mediaName, mediaPath])

  const fileTypes = useMemo(() => {
    const checkExtension = extensions => {
      const regex = new RegExp(`\\.(${extensions.join('|')})$`, 'i')
      if (derivedMediaName) return !!derivedMediaName.match(regex)
      if (mediaPath) return !!mediaPath.split('?')[0].match(regex)
      return false
    }

    return {
      isImage: type !== 'audio' && checkExtension(imageExtensions),
      isAudio: type !== 'image' && checkExtension(audioExtensions)
    }
  }, [derivedMediaName, mediaPath, type, imageExtensions, audioExtensions])

  const acceptedFormatsString = useMemo(() => {
    if (type === 'image')
      return imageExtensions.map(ext => `.${ext}`).join(',') + ',image/*'
    if (type === 'audio')
      return audioExtensions.map(ext => `.${ext}`).join(',') + ',audio/*'

    const images = imageExtensions.map(ext => `.${ext}`).join(',')
    const audios = audioExtensions.map(ext => `.${ext}`).join(',')
    return `${images},${audios},image/*,audio/*`
  }, [type, imageExtensions, audioExtensions])

  const formatsLabel = useMemo(() => {
    if (type === 'image') return imageExtensions.join(', ').toUpperCase()
    if (type === 'audio') return audioExtensions.join(', ').toUpperCase()
    return [...imageExtensions, ...audioExtensions].join(', ').toUpperCase()
  }, [type, imageExtensions, audioExtensions])

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      const previewUrl = URL.createObjectURL(file)
      onFileSelect(file, previewUrl)
    }
  }

  const handleClear = e => {
    e.preventDefault()
    e.stopPropagation()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onFileRemove()
  }

  return (
    <>
      <Box
        component="section"
        aria-label="Upload de Anexo"
        className={classes.boxAttachment}
      >
        <Typography variant="body2" component="span">
          {label || 'Anexo'}
        </Typography>

        <Box className={classes.boxAttachmentContainer}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept={acceptedFormatsString}
          />

          <ButtonBase
            onClick={() => fileInputRef.current?.click()}
            component="div"
            focusRipple
            aria-label={
              derivedMediaName
                ? `Alterar anexo atual: ${derivedMediaName}`
                : 'Selecionar um arquivo de anexo'
            }
            className={classes.button}
          >
            {mediaPath ? (
              <Box className={classes.attachmentPreviewImage}>
                {fileTypes.isImage && (
                  <Box
                    component="div"
                    role="button"
                    tabIndex={0}
                    aria-label="Expandir visualização da imagem"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      setIsPreviewOpen(true)
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        setIsPreviewOpen(true)
                      }
                    }}
                    style={{
                      cursor: 'zoom-in',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <img
                      src={getMediaUrl(mediaPath)}
                      alt="Miniatura do anexo"
                      className={classes.coverImage}
                      onError={e => {
                        e.target.style.display = 'none'
                      }}
                    />
                  </Box>
                )}

                {fileTypes.isAudio && (
                  <Box
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    style={{
                      width: '100%',
                      maxWidth: size === 'small' ? '120px' : '200px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <AudioPlayer
                      audioUrl={getMediaUrl(mediaPath)}
                      variant="compact"
                      showTime={size !== 'small'}
                      isMessageRight={false}
                    />
                  </Box>
                )}

                {!fileTypes.isImage && !fileTypes.isAudio && (
                  <Paperclip size={size === 'small' ? 14 : 16} />
                )}

                {!fileTypes.isAudio && showMediaName && (
                  <Typography
                    variant="body2"
                    className={classes.attachmentName}
                  >
                    {derivedMediaName || `Visualizar anexo de ${displayName}`}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box className={classes.attachmentNotFile}>
                <Paperclip size={size === 'small' ? 14 : 16} />
                <Typography variant="body2">
                  Clique para anexar um arquivo
                </Typography>
              </Box>
            )}
          </ButtonBase>

          <Box
            className={classes.boxIconButtonDelete}
            style={{ visibility: mediaPath ? 'visible' : 'hidden' }}
          >
            {mediaPath && (
              <IconButton
                onClick={handleClear}
                aria-label={`Remover anexo de ${displayName}`}
                className={classes.iconButtonDelete}
                size="small"
              >
                <Trash2 size={size === 'small' ? 16 : 18} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" width="100%">
          <Typography variant="caption" className={classes.attachmentFiles}>
            Formatos: {formatsLabel}
          </Typography>
          {helperText && (
            <Typography variant="caption" className={classes.attachmentFiles}>
              {helperText}
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={isPreviewOpen && fileTypes.isImage}
        onClose={() => setIsPreviewOpen(false)}
        aria-label="Visualização do anexo ampliado"
      >
        <DialogContent className={classes.dialogContent}>
          <Grid container className={classes.boxPreviewImage}>
            <img
              src={getMediaUrl(mediaPath)}
              alt="Anexo Ampliado"
              className={classes.zoomImage}
            />
            <span>{derivedMediaName}</span>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <ButtonSecondary
            label="Fechar"
            onClick={() => setIsPreviewOpen(false)}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
